import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { videos } from '@/mocks/videos';
import { categories } from '@/mocks/categories';
import VideoCard from '@/components/feature/VideoCard';
import VideoPlayer from '@/pages/detail/components/VideoPlayer';
import { formatDuration, formatViews } from '@/utils/format';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

export default function DetailPage() {
  const { videoId = '' } = useParams();
  const video = videos.find((v) => v.id === videoId);
  const [copied, setCopied] = useState(false);

  const related = useMemo(() => {
    if (!video) return [];
    return videos
      .filter((v) => v.id !== video.id)
      .map((v) => {
        let score = 0;
        if (v.categoryId === video.categoryId) score += 2;
        score += v.tags.filter((t) => video.tags.includes(t)).length;
        return { v, score };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 6)
      .map((x) => x.v);
  }, [video]);

  const category = video ? categories.find((c) => c.id === video.categoryId) : undefined;

  useDocumentTitle(
    video ? `${video.title} - 靠逼视频` : '视频详情 - 靠逼视频',
    video ? video.description.slice(0, 120) : undefined,
  );

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  if (!video) {
    return (
      <div className="w-full px-4 md:px-8 py-20 text-center">
        <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-500 text-3xl">
          <i className="ri-movie-2-line"></i>
        </span>
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">视频不存在或已下线</h1>
        <p className="mt-2 text-sm text-foreground-600">你访问的视频可能已被移除，看看其他精彩内容吧。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 h-10 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap"
        >
          <i className="ri-home-4-line"></i> 返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-10 animate-fade-up">
      <nav className="flex items-center flex-wrap gap-2 text-xs text-foreground-600 mb-5" aria-label="面包屑">
        <Link to="/" className="hover:text-primary-400 transition-colors">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to={`/category/${video.categoryId}`} className="hover:text-primary-400 transition-colors">
          {category?.name ?? '分类'}
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-900 truncate max-w-[180px] md:max-w-none">{video.title}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
        <div className="lg:col-span-2">
          <h1 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 leading-snug mb-4">
            {video.title}
          </h1>

          <VideoPlayer video={video} />

          <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-foreground-600">
            <span className="flex items-center gap-2">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500/15 text-primary-400">
                <i className="ri-user-3-line"></i>
              </span>
              <span className="text-foreground-900 font-medium">{video.uploader}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {video.uploadDate}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line"></i>
              {formatViews(video.views)} 次观看
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              {formatDuration(video.duration)}
            </span>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleShare}
              className="relative h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium flex items-center gap-2 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className={copied ? 'ri-check-line' : 'ri-share-forward-line'}></i>
              {copied ? '链接已复制' : '复制分享链接'}
            </button>
            <Link
              to={`/category/${video.categoryId}`}
              className="h-10 px-5 rounded-md bg-background-100 border border-background-300 hover:border-primary-400 text-foreground-900 text-sm font-medium flex items-center gap-2 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回{category?.name ?? ''}分类
            </Link>
            {copied && (
              <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-accent-100 text-accent-900 text-xs">
                <i className="ri-links-line"></i>
                分享链接已复制到剪贴板
              </span>
            )}
          </div>

          <div className="mt-6 bg-background-100 border border-background-200 rounded-lg p-5 md:p-6">
            <h2 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-3">视频简介</h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{video.description}</p>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-600 mr-1">标签：</span>
              {video.tags.map((t) => (
                <Link
                  key={t}
                  to={`/search?q=${encodeURIComponent(t)}`}
                  className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs hover:bg-secondary-200 transition-colors"
                >
                  #{t}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <aside className="lg:col-span-1">
          <div className="bg-background-100 border border-background-200 rounded-lg p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950 mb-3">内容信息</h2>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center justify-between">
                <span className="text-foreground-600">视频分类</span>
                <Link to={`/category/${video.categoryId}`} className="text-foreground-900 hover:text-primary-400 transition-colors">
                  {category?.name ?? '—'}
                </Link>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-foreground-600">子分类</span>
                <span className="text-foreground-900">{video.subCategory}</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-foreground-600">上传者</span>
                <span className="text-foreground-900">{video.uploader}</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-foreground-600">视频时长</span>
                <span className="text-foreground-900">{formatDuration(video.duration)}</span>
              </li>
            </ul>
            <div className="mt-4 p-3 rounded-md bg-accent-100 text-accent-900 text-xs leading-relaxed">
              <i className="ri-information-line mr-1"></i>
              本站视频为演示内容，播放器仅演示加载与失败状态，不提供真实播放。
            </div>
          </div>
        </aside>
      </div>

      <section className="mt-12 md:mt-16">
        <div className="flex items-end justify-between mb-5 md:mb-7">
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">相关推荐</h2>
            <p className="mt-1 text-sm text-foreground-600">根据分类与标签为你推荐</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {related.map((v) => (
            <VideoCard key={v.id} video={v} />
          ))}
        </div>
      </section>
    </div>
  );
}